import { useMemo } from 'react';

import type { SlideoutEntry } from '@/lib/crm/contexts/SlideoutStackContext';
import type { ProposalDetail } from '@/lib/proposals/core/types';

import type { TriggerButtonTarget } from './ActionOverview.helpers';
import {
  encodeEmailThreadEntryId,
  findEmailActionForThread,
} from './emailThreadEntryId';
import { useTriggerShortcutButtons } from './useTriggerShortcutButtons';

export type AutoOpenTarget =
  | { kind: 'entity'; entityId: string }
  | { kind: 'thread-overlay'; proposalId: number; actionId: number };

/**
 * The slideout-stack entry a target resolves to — shared by
 * `AutoOpenFirstRecord` (push) and ActionDetailPage's switch-veil seeding
 * (setStack), so the two can never disagree about what a target opens.
 */
export function slideoutEntryForAutoOpenTarget(
  target: AutoOpenTarget
): SlideoutEntry {
  switch (target.kind) {
    case 'entity':
      return { kind: 'entity', id: target.entityId };
    case 'thread-overlay':
      return {
        kind: 'email_thread',
        id: encodeEmailThreadEntryId(target.proposalId, target.actionId),
      };
    default:
      return assertNeverTarget(target);
  }
}

function assertNeverTarget(target: never): never {
  throw new Error(`Unhandled auto-open target: ${JSON.stringify(target)}`);
}

export type AutoOpenTargetResult =
  | { status: 'resolving' }
  | { status: 'settled'; target: AutoOpenTarget | null };

/**
 * Derives "what would populate the panel" for a proposal — the single
 * decision behind both the persistent-panel gate (does this action have an
 * inline target at all?) and AutoOpenFirstRecord (what should it push?).
 * Sharing this derivation is what keeps the two from disagreeing about
 * whether an action is still resolving or has nothing to show.
 *
 * Wraps `useTriggerShortcutButtons` — the same derivation behind the header's
 * "View X" buttons (React Query dedupes the underlying trigger fetch) — plus
 * the `findEmailActionForThread` fallback for proposals with no trigger
 * buttons at all.
 */
export function useAutoOpenTarget(
  action: ProposalDetail
): AutoOpenTargetResult {
  const {
    isLoading,
    buttons,
    isResolvingParticipants,
    firstTriggerEmailThreadEntityId,
  } = useTriggerShortcutButtons(action);

  return useMemo<AutoOpenTargetResult>(() => {
    if (isLoading) {
      return { status: 'resolving' };
    }
    const firstEntityTarget = buttons
      .map(button => button.target)
      .find(
        (target): target is Extract<TriggerButtonTarget, { kind: 'entity' }> =>
          target.kind === 'entity'
      );
    if (firstEntityTarget) {
      return {
        status: 'settled',
        target: { kind: 'entity', entityId: firstEntityTarget.entityId },
      };
    }
    if (isResolvingParticipants) {
      // A "View {contact}" button can still resolve from the CRM store —
      // keep waiting rather than deciding on an incomplete set.
      return { status: 'resolving' };
    }
    // Settled with no entity buttons: the header renders no trigger buttons.
    // Fall back to the proposal's source email thread, in two shapes:
    // 1. a CRM-event trigger whose entity IS an email thread (the trigger
    //    card's record — e.g. an agent proposal built from synced threads)
    //    is itself an entity target;
    // 2. else a gmail_send action replying on a thread opens the
    //    email-thread overlay slideout.
    if (firstTriggerEmailThreadEntityId) {
      return {
        status: 'settled',
        target: { kind: 'entity', entityId: firstTriggerEmailThreadEntityId },
      };
    }
    const emailActionForThread = findEmailActionForThread(action);
    if (!emailActionForThread) {
      return { status: 'settled', target: null };
    }
    return {
      status: 'settled',
      target: {
        kind: 'thread-overlay',
        proposalId: action.id,
        actionId: emailActionForThread.id,
      },
    };
  }, [
    isLoading,
    buttons,
    isResolvingParticipants,
    firstTriggerEmailThreadEntityId,
    action,
  ]);
}
